import { useTranslation } from 'next-i18next'
// Dependencies.
import { useState, useMemo, useEffect, useContext } from 'react'
import { useRouter } from 'next/router'
import styled from 'styled-components'

import { EventHookInterface, Option } from '../../../types'
import { SectionField } from '../../Inputs/SectionField'
import { UserContext } from '../../../context'
import { EmailInvite } from '../EmailInvite'

const DIV = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: -moz-available;
  width: -webkit-fill-available;
  width: fill-available;

  & .selectComponent {
    width: 100%;

    & .selectInput {
      width: 100%;
    }
  }
`

// Props.
interface Props {
  eventHook: EventHookInterface
}

// Who To Invite.
export const WhoToInvite: React.FC<Props> = (props) => {
  const { t } = useTranslation('common')

  // Router.
  const router = useRouter()
  const showWhen =
    router.pathname === '/events/create' ||
    router.pathname === '/events/duplicate/[eventId]'

  // Event.
  const { eventHook } = props
  const { isEditable } = eventHook
  const event = eventHook.value

  // Context
  const { socialContacts } = useContext(UserContext)

  // State
  const [contactOption, setContactOption] = useState<Option[]>([])

  // What To Bring.
  const whoToInvite = useMemo(() => {
    if (!event.invites || !event.invites.length)
      return [{ event_id: event.id, email: '' }] // Empty String Only.
    const lastItemIsEmptyString =
      event.invites[event.invites.length - 1].email === ''
    if (!lastItemIsEmptyString)
      return [...event.invites, { event_id: event.id, email: '' }]
    return [...event.invites]
  }, [event])

  useEffect(() => {
    if (socialContacts) {
      const options = socialContacts.map((contact) => {
        return {
          label: `${contact.name} (${contact.email})`,
          value: contact.email,
        }
      })
      setContactOption(options)
    }
  }, [socialContacts])

  // Add Item Add Index.
  const addItemAtIndex = (index: number) => {
    eventHook.updateFieldsWithPartial({
      invites: [
        ...whoToInvite.slice(0, index + 1),
        { event_id: event.id, email: '' },
        ...whoToInvite.slice(index + 1),
      ],
    })
  }

  // Handle Backspace At Index.
  const handleBackspaceAtIndex = (index: number) => {
    const invites = [...whoToInvite]
    const target = invites[index]
    if (!target.email) invites.splice(index, 1)
    eventHook.updateFieldsWithPartial({ invites })
  }

  // On Change At Index.
  const onSelectChangeAtIndex = (event: Option, index: number) => {
    const invites = [...whoToInvite]
    if (event) {
      invites[index].email = event.value
    } else {
      invites[index].email = ''
    }
    eventHook.updateFieldsWithPartial({ invites })
  }

  // On Change At Index.
  const onInputChangeAtIndex = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const invites = [...whoToInvite]
    invites[index].email = event.target.value
    eventHook.updateFieldsWithPartial({ invites })
  }

  // On Key Down At Index.
  const onKeyDownAtIndex = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    const backspaceWasPressed = event.keyCode === 8
    const enterKeyWasPressed = event.keyCode === 13
    if (backspaceWasPressed) return handleBackspaceAtIndex(index)
    if (enterKeyWasPressed) return addItemAtIndex(index)
  }

  // hide if not a create page
  if (showWhen === false) return null
  // ..
  return (
    <SectionField
      header={t(`Who to invite`, `Who to invite`)}
      subheader={t(
        `WhoToInvite758007102`,
        `Add the emails of those you would like to invite and the details will be sent to these individuals. We will also provide a link for you to share on the next page.`,
      )}>
      <EmailInvite eventHook={eventHook}></EmailInvite>
    </SectionField>
  )
}
